import "./KForm.scss";
import { Redirectable } from "kinnect-mixins";
import {
	VAlert,
	VBtn,
	VCard,
	VCardActions,
	VCardText,
	VCardTitle,
	VDivider,
	VForm,
	VSpacer
} from "vuetify/lib";
import { ValidationObserver } from "vee-validate";

export default VForm.extend( {
	name: "KForm",

	mixins: [ Redirectable ],

	props: {
		action: String,

		cancelButtonText: {
			type: [ String, Boolean ],
			default: "Cancel"
		},

		data: [ FormData, Object ],

		formTitle: String,

		loading: Boolean,

		manualSubmitCall: Function,

		method: String,

		readonly: Boolean,

		submitButtonText: {
			type: String,
			default: "Save"
		},

		successMessage: {
			default: "Saved",
			type: [ Boolean, String ]
		},

		successTimeout: {
			default: null,
			type: [ Number, String ]
		}
	},

	data: () => ( {
		isSaving: false
	} ),

	computed: {
		currentBreadcrumb() {
			return this.$wait.is( "get breadcrumbs" )
				? { text: "\u00A0" }
				: this.$store.getters["breadcrumbs/current"];
		},

		title() {
			if ( this.formTitle ) {
				return this.formTitle;
			} else if ( this.currentBreadcrumb ) {
				return this.currentBreadcrumb.text;
			}
		},

		parentDialog() {
			let parent = this.$parent;

			while ( parent ) {
				if ( parent.$options.name === "v-dialog" ) {
					return parent;
				}

				parent = parent.$parent;
			}

			return false;
		},

		genCardTitle() {
			if ( this.title ) {
				return [
					<VCardTitle class="primary white--text">
						{this.title}
					</VCardTitle>,
					<VSpacer></VSpacer>
				];
			}
		},

		genCardActions() {
			if ( !this.readonly ) {
				return [
					<VDivider class="mt-4 d-print-none"></VDivider>,

					<VCardActions class="d-print-none">
						{this.$slots.actions
							? this.$slots.actions
							: [
								Boolean( this.cancelButtonText ) && <VBtn
									large
									onClick={this.handleRedirect}
									text
								>{this.cancelButtonText}</VBtn>,

								<VSpacer></VSpacer>,

								<VBtn
									color="link"
									large
									loading={this.isSaving}
									text
									type="submit"
								>{this.submitButtonText}</VBtn>
							]
						}
					</VCardActions>
				];
			}
		}
	},

	methods: {
		clearForm() {
			this.$refs.observer.reset();
		},

		isFrontEndValidationError( error ) {
			return error && error.name && error.name === "ValidationError";
		},

		isBackEndValidationError( error ) {
			return error && error.response && error.response.data && error.response.data.errors;
		},

		assignErrors( errors ) {
			this.$refs.observer.setErrors( errors );
		},

		async scrollToFirstError() {
			await this.$nextTick();

			const errorFieldEl = this.$el.querySelector( ".v-input--has-state.error--text" );

			if ( errorFieldEl ) {
				this.$vuetify.goTo( errorFieldEl, 600, {
					cancelable: false,
					container: this.parentDialog,
					duration: 600,
					offset: this.parentDialog
						? null
						: -( this.$vuetify.application.top + this.$vuetify.application.bar + 50 )
				} );
			}
		},

		handleRedirect() {
			if ( this.parentDialog ) {
				return this.$emit( "close" );
			}

			return this.doRedirect();
		},

		async validateAndSubmit() {
			if ( this.readonly ) {
				return;
			}

			const isValid = await this.$refs.observer.validate();

			if ( isValid ) {
				this.submit();
			} else if ( this.$refs.observer.errors ) {
				this.scrollToFirstError();
			} else {
				this.$store.commit( "toasts/SHOW", {
					color: "error",
					text: "Please correct the form errors and try again"
				} );
			}
		},

		async submit() {
			let httpCall;

			if ( this.method && this.action && this.data ) {
				httpCall = this.$http[this.method](
					this.action,
					this.data,
					this.data instanceof FormData
						? { headers: { "Content-Type": "multipart/form-data" } }
						: null
				);
			} else if ( this.manualSubmitCall ) {
				httpCall = this.manualSubmitCall();
			}

			if ( httpCall ) {
				this.isSaving = true;

				try {
					const response = await httpCall;

					this.$emit( "success", response );
					this.handleSuccess();
					this.handleRedirect();
					this.clearForm();
				} catch ( error ) {
					if ( this.isFrontEndValidationError( error ) ) {
						this.scrollToFirstError();
					} else if ( this.isBackEndValidationError( error ) ) {
						this.assignErrors( error.response.data.errors );
						this.scrollToFirstError();
					}
				} finally {
					this.isSaving = false;
				}
			} else {
				this.handleRedirect();
				this.clearForm();
			}
		},

		handleSuccess() {
			if ( this.successMessage ) {
				return this.$store.commit( "toasts/SHOW", {
					color: "success",
					text: this.successMessage,
					timeout: this.successTimeout
				} );
			}
		}
	},

	render() {
		return <ValidationObserver
			ref="observer"
			slim
			scopedSlots={{
				default: () =>
					<VForm
						class="k-form mx-auto"
						onSubmit={e => {
							e.preventDefault();
							this.validateAndSubmit();
						}}
						ref="form"
					>
						<VCard loading={this.loading ? "link" : false}>
							{this.genCardTitle}

							<VCardText>
								{this.readonly && <VAlert type="info">This form is in read-only mode and changes cannot be saved</VAlert>}

								{this.$slots.default}
							</VCardText>

							{this.genCardActions}
						</VCard>
					</VForm>
			}}
		></ValidationObserver>;
	}
} );
